/**
 * intake-step-list.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 */

import {
  Component,
  ExistingProvider,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  OpaqueToken
} from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, Validators }
  from "@angular/forms";
import { Observable, Subscription } from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { AnswerProxy } from "../algorithms/management/answer-proxy";
import { IntakeStepService }
  from "../algorithms/management/intake-step.service";
import { ReceivedAnswer, StepDefinition, StepUpdate }
  from "../algorithms/management/pipeline/step-update";
import { ReportStepService }
  from "../algorithms/management/report-step.service";
import { IntakeStep } from "../algorithms/structure/intake-step";
import { ReportStep } from "../algorithms/structure/report-step";
import { AlertService } from "../common/alert.service";
import { TypedData, TypedDataHolder } from "../common/data/typed-data";
import { SettingService } from "../configuration/setting.service";
import { ExplanationModalComponent }
  from "../debug/explanation-modal.component";
import { JsonModalComponent }
  from "../debug/json-modal.component";
import { ExplanationGraph } from "../graphs/explanation-graph";
import { GraphService } from "../graphs/graph.service";
import {
  AcknowledgmentResponse,
  Message,
} from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { ValidationMessages }
    from "../support/controls/validation-messages.component";
import { ModalHelpers, Tree, TreeNode } from "../support/utilities";
import { inputless, inverted, valueless } from "../support/utilities/functions";
import { JumpToStepModalComponent } from "./jump-to-step-modal.component";
import { ValidationBuilder } from "./validation.builder";

import {
  ControlGroup,
  ControlGroupType,
  OrderedControlGroup,
  TabularControlGroup
} from "../common/data/control-group";

import { Modal } from "angular2-modal/plugins/bootstrap";

const ANSWER_PROVIDER : ExistingProvider = {
  provide: AnswerProxy,
  useExisting: forwardRef(() => IntakeStepListComponent)
};

/**
 * An {@link IntakeStepListComponent} presents a list of {@link IntakeStep}s
 * obtained from an {@link IntakeStepService}.
 */
@Component({
  selector: "intake-step-list",
  template: `
    <spinner *ngIf="(!groups || !groups.length)"></spinner>
    <control-group
      *ngFor="let group of groups"
      [group]="group"
      (action)="handleAction($event)">
    </control-group>
    <div class="watermark">WORKING DRAFT</div>
    <!--<div class="more-button">
      <button
        type="button"
        class="btn btn-default btn-lg"
        (click)="requestMore()"
        [disabled]="!context?.algorithmContext?.stepsAvailable"
        tabindex="-1">
        Load more questions
      </button>
    </div>-->
  `,
  styles: [
    `spinner {font-size: 1.5em; margin: 2em;}
    /deep/ .intake-step {position: relative; max-width: 98vw;}
    .more-button {text-align: center; padding-top: 1.5em;}
    .a-first-check.wi /deep/ control-group+control-group .classic {
      border-top: 2px dashed;
      padding-top: 20px;
      margin-top: 10px;
    }
    .watermark {display: none;}
    :host.a-first-check-wi .watermark {
      display: block;
      position: absolute;
      top: 40px;
      left: -20px;
      width: 100%;
      margin: auto;
      text-align: center;
      color: red;
      font-family: sans-serif;
      font-size: x-large;
      text-shadow: 0px 0px 10px gray;
      transform: rotate(-30deg);
    }
    .special-income-table {
      width: 100%;
    }
    .special-income-table th {
      text-align: center;
    }
    `
  ],
  providers: [ANSWER_PROVIDER]
})
export class IntakeStepListComponent implements AnswerProxy, OnInit, OnDestroy
{
  /**
   * A memo of the subscriptions this component holds.
   */
  private sub : Subscription;

  /**
   * The set of all intake groups present in this list.
   */
  public groups : ControlGroup[];

  private tableGroups : Map<string, TabularControlGroup> =
    new Map<string, TabularControlGroup>();
  private orderedGroups : OrderedControlGroup[] = [];

  /**
   * A map from the IDs of intake steps to the groups those steps have been
   * assigned to (if any).
   */
  private groupAssignment : Map<number, ControlGroup> =
    new Map<number, ControlGroup>();

  // TODO: phase this out as a setting
  public readonly tablesEnabled : boolean = true;

  /**
   * Steps that were autofilled and are waiting to be sent to the server as a
   * batch. This is necessary because there's no guarantee of the number of
   * messages the server will send about a "batch" of new steps, so we must
   * debounce the incoming definitions and wait to send the autofilled answers
   * until it's likely the server is done, to prevent thrashing back and forth
   * with changes while the engine is still executing.
   */
  public waitingAutofills : IntakeStep[] = [];

  @Input()
  public form : FormGroup;

  /**
   * Construct a new {@link IntakeStepListComponent}.
   *
   * @param intakeStepService
   *        The {@link IntakeStepService} responsible for providing {@link
   *        IntakeStep}s.
   */
  constructor (
    private intakeService : IntakeStepService,
    private reportService : ReportStepService,
    private algorithms : AlgorithmService,
    private messaging : MessagingService,
    private settings : SettingService,
    public alerts : AlertService,
    private graphs : GraphService,
    private modal : Modal
  )
  {
    /* Nothing. */
  }

  /**
   * Arrange to receive asynchronous {@link IntakeStep}s from the {@link
   * IntakeStepService}.
   */
  public ngOnInit () : void
  {
    if (!this.algorithms.isRunning)
    {
      throw new Error("No algorithm context initialized");
    }

    this.sub = this.intakeService.updates().subscribe(
      // tslint:disable-next-line:cyclomatic-complexity
      (u : StepUpdate<any, IntakeStep>) => {
        if (u instanceof StepDefinition && u.source instanceof IntakeStep)
        {
          this.form.addControl(u.source.name, u.source.control);
          if (u.effects.autofilled && u.effects.autofillTarget)
          {
            /**
             * SUPER IMPORTANT NOTE: The source of a StepDefinition is only
             * going to be the "real" target step on the first receipt. On
             * subsequent executions, it's just a temporary object used to
             * transfer properties to the real thing (which lives in the
             * StepManager's map). This isn't a "problem" because most of the
             * effects here degrade gracefully without access to things only
             * the real step would have (in particular, its AbstractControl).
             * However the autofiller needs the real step to be inserted into
             * its list of pending autofill updates for a batch answer, as well
             * as the real control in order to mark as dirty.
             * As a temporary fix until the whole pipeline can be improved, the
             * autofiller manually attaches these items as TransformationFlags
             * so that they can be used below.
             * TODO TODO TODO [TDHS]
             */
            this.waitingAutofills.push(u.effects.autofillTarget as IntakeStep);
            u.effects.autofillTarget.control.markAsDirty();
          }
          if (u.source.control && u.source.differsFromOriginalValue)
          {
            u.source.control.markAsDirty();
            u.source.provider = "a collaborator before you joined";
          }
        }
        if (u instanceof ReceivedAnswer && u.source.provider)
        {
          const target : IntakeStep = this.intakeService.get(u.id);
          target.provider = u.source.provider;
          target.control.markAsDirty();
          if (u.source.value === null)
          {
            this.alerts.userInfo(
              `${u.source.provider} cleared the value of
              ${target.displayName || "unknown"}`
            );
          }
          else
          {
            this.alerts.userInfo(
              `${u.source.provider} submitted an answer for
              <em>${target.displayName || "unknown"}</em>`
            );
          }
        }
      }
    );

    this.sub.add(this.intakeService.updatingSet().debounceTime(400)
      .subscribe((steps : IntakeStep[]) => {
        // update the groups
        this.groupSteps(steps);

        // submit waiting autofills as a batched answer
        if (this.waitingAutofills.length > 0) {
          this.submitMultiple(this.waitingAutofills, [new Event("autofill")]);
          this.waitingAutofills = [];
        }
      }
    ));

    this.sub.add(this.reportService.updatingSet().debounceTime(400).subscribe(
      (steps : ReportStep[]) => this.injectInlineReports(steps)));

    this.groups = [];
    this.newGroup("ordered", "", {order: 1});
  }

  /**
   * Assign unseen steps to groups as necessary.
   * @param steps received steps, including both seen & unseen
   */
  private groupSteps (steps : IntakeStep[]) : void
  {
    steps.filter(
      (step : IntakeStep) => !this.groupAssignment.get(step.id)
    ).forEach(
      (step : IntakeStep) => this.newStep(step)
    );
    console.info("update:", this.groups, this.tableGroups, this.orderedGroups);
  }

  /* hack for FCWI inlining */
  protected allReportsInline : boolean =
    this.algorithms.running.name === "a-first-check-wi";

  /**
   * Take all {@link ReportStep}s hinted as "inline" and inject them into the
   * grouping(s) as {@link IntakeStep}s with type="empty".
   * @param steps list of reports
   */
  private injectInlineReports (steps : ReportStep[]) : void
  {
    this.groupSteps(
      steps.filter(
        (step : ReportStep) =>
          (this.allReportsInline || step.hints.inline)
          && step.isActive
      ).map(
        (step : ReportStep) => step.asEmptyIntake()));
  }

  private newGroup (
    type : ControlGroupType = null,
    name? : string,
    config : {order? : number, max? : number, min? : number} = {}
  ) : ControlGroup
  {
    let group : ControlGroup;
    if (type === "tabular" && this.tablesEnabled)
    {
      const gridColumnMax : number = this.settings.get("table column max");
      group = new TabularControlGroup(name, gridColumnMax);
      this.tableGroups.set(name, group as TabularControlGroup);
      if (config.order)
      {
        const toSplit : OrderedControlGroup = this.findOrdered(config.order);
        if (toSplit)
        {
          const upperPart : OrderedControlGroup =
            new OrderedControlGroup(config.order + 1, toSplit.max);
          this.orderedGroups.push(upperPart);
          toSplit.setMax(config.order - 1).forEach((s : TypedDataHolder) => {
            upperPart.add(s);
            this.groupAssignment.set(s.id, upperPart);
          });

          this.groups.splice(
            this.groups.indexOf(toSplit) + 1, 0, group, upperPart);
          return group;
        }
      }
    }
    else if (type === "ordered")
    {
      group = new OrderedControlGroup(config.min, config.max);
      this.orderedGroups.push(group as OrderedControlGroup);
    }
    else
    {
      group = new ControlGroup(type, name);
    }

    this.groups.push(group);
    return group;
  }

  private readonly FORCE_IGNORE : string[] = [
    "q-fcwi-estimated-unearned-monthly-income",
    "q-fcwi-estimated-earned-monthly-household",
    "q-fcwi-estimated-unearned-monthly-household"
  ];
  private newStep (step : IntakeStep) : void
  {
    if (this.FORCE_IGNORE.indexOf(step.name) > -1) {
      console.warn(`Ignoring step ${step.name} due to FORCE_IGNORE`);
      return;
    }
    if (step.hints.table && this.tablesEnabled)
    {
    //   console.log(step.hints.table, this.tableGroups.keys());
      if (!this.tableGroups.has(step.hints.table))
      {
        this.newGroup("tabular", step.hints.table, {order: step.order});
      }
      this.addToGroup(step, step.hints.table);
    }
    else if (IntakeStep.ordered(step))
    {
      this.addToGroup(step, "ordered");
    }
    else
    {
      this.addToGroup(step);
    }
  }

  private addToGroup (step : IntakeStep, target? : string | number) : void
  {
    let group : ControlGroup;
    if (typeof target === "string")
    {
      if (target === "ordered")
      {
        group = this.findOrdered(step.order);
        if (!group)
        {
          console.error(step, this.orderedGroups);
          throw new Error(`Could not find group for order ${step.order}`)
 ;       }
      }
      else
      {
        group = this.tableGroups.get(target);
      }
    }
    else if (Number.isInteger(target))
    {
      group = this.groups[target];
    }
    else
    {
      group = this.groups.slice(-1)[0];
      if (group.name)
      {
        group = this.newGroup();
      }
    }

    if (!group)
    {
      console.error(step, this.groups);
      throw new Error(`Could not add to group target ${target}, unknown group`);
    }

    if (!group.add(step))
    {
      console.error(step, group);
      throw new Error(`Group is full or prohibits steps of this type`);
    }

    this.groupAssignment.set(step.id, group);
  }

  private findOrdered (order : number) : OrderedControlGroup
  {
    for (const group of this.orderedGroups)
    {
      if (order >= group.min && order <= group.max)
      {
        return group;
      }
    }
    return null;
  }

  private removeBlanks (list : any[]) : any[]
  {
    if (Array.isArray(list))
    {
      // TODO: ok now that we have nulls, this is valid
      list = list.filter((x : any) => !valueless(x));
    }
    return list;
  }
  private collapseListBlanksToNulls (list : any[]) : any[]
  {
    if (Array.isArray(list))
    {
      list = list.map((x : any) => inputless(x) ? null : x);
    }
    return list;
  }
  private removeEndNulls (list : any[]) : any[]
  {
    let i : number;
    for (i = list.length; i > 0; i--)
    {
      if (list[i - 1] !== null)
      {
        break;
      }
    }
    return list.slice(0, i);
  }

  private prepareForSubmission (step : IntakeStep) : void
  {
    if (!(step instanceof IntakeStep) || !step.control)
    {
      console.error("non-intake / control-less step:", step);
      throw new Error();
    }
    if (!ValidationMessages.submittable(step.control))
    {
      // || (step.control.disabled && e.type !== "autofill"))
      // TODO: diversify messages
      this.alerts.warn(`Cannot submit ${step.name}: invalid value for step!`);
      console.log(step.control.value, step.control.errors);
      return;
    }
    console.log("Submitting answer", step);
    step.value = step.control.value;
    step.provider = null;

    // step.value = this.removeBlanks(step.value);
    if (Array.isArray(step.value))
    {
      step.value = this.removeEndNulls(
        this.collapseListBlanksToNulls(step.value));
    }
    console.info("collapsed value", step.value);
  }

  public submitByName (name : string, e? : Event) : Observable<Message>
  {
    const step : IntakeStep = this.intakeService.findNamed(name);
    return this.submitAnswer(step, e);
  }

  public submitRaw (name : string, value : any) : Observable<Message>
  {
    const step : IntakeStep = this.intakeService.findNamed(name);
    if (!step) {
      console.error(`Step ${name} is unknown! Skipping submission`);
      return Observable.never();
    }
    step.value = value;
    step.provider = null;
    console.warn("Submitting RAW answer", step);
    const o : Observable<Message> =
      this.intakeService.submit(step.completedIntakeStep());
    o.subscribe();
    return o;
  }

  public submitAnswer (step : IntakeStep, e? : Event) : Observable<Message>
  {
    this.prepareForSubmission(step);
    const o : Observable<Message> =
      this.intakeService.submit(step.completedIntakeStep());
    o.subscribe();
    return o;
  }

  public submitMultiple (steps : IntakeStep[], e? : Event[])
    : Observable<Message>
  {
    steps.forEach((step : IntakeStep) => this.prepareForSubmission(step));
    const o : Observable<Message> =
      this.intakeService.submit(
        steps.map((step : IntakeStep) => step.completedIntakeStep()));
    o.subscribe();
    return o;
  }

  public ngOnDestroy () : void
  {
    // Nothing
  }

  public requestMore () : void
  {
    // TODO: Presumably this will be able to handle some info about whether
    // there are more steps or not? (Or maybe that was scrapped.) But I'll
    // need to know more about the responses to make them self-verifying so
    // for now the subscription will do nothing.
    this.intakeService.requestMore().subscribe(
      (m : AcknowledgmentResponse) => {
        if (!m.ok)
        {
          this.alerts.notify(
            "The server does not have any more steps available at this time");
        }
      });
  }

  public handleAction ([name, args] : [string, any]) : void
  {
    if (name === "explain")
    {
      return this.explain(args);
    }
    if (name === "details")
    {
      return this.details(args);
    }
    console.error(name, args);
    throw new Error("Unknown group action occurred!");
  }

  public explain (step : IntakeStep) : void
  {
    this.graphs.explainStep(step).subscribe((explanation : ExplanationGraph) =>
      ModalHelpers.openCustom(
        this.modal,
        ExplanationModalComponent,
        {explanation}
          ));
  }
  public details (step : IntakeStep) : void
  {
    Object.defineProperty(step, "control", {enumerable: false});
    Object.defineProperty(step.hints, "_group", {enumerable: false});
    console.info("Suppressed control value:", step.control);
    ModalHelpers.openCustom(
      this.modal,
      JsonModalComponent,
      {
        object: step,
        note: `control and _group may have been suppressed to reduce clutter`
      }
    );
  }

  @HostListener("document:keydown.control.j", ["$event"])
  @HostListener("document:keydown.meta.j", ["$event"])
  public jumpToDialog (event : KeyboardEvent) : void
  {
    event.preventDefault(); // Ctrl+J is a default shortcut on Chrome for Win
    ModalHelpers.observeCustom(
      this.modal,
      JumpToStepModalComponent,
      {groups: this.groups, size: "med"}
    ).subscribe((id : number) => {
      if (id !== null)
      {
        const e : HTMLElement = document.getElementById(`${id}`);
        if (e)
        {
          e.scrollIntoView(
            {behavior: "smooth", block: "center", inline: "nearest"});
        }
        else
        {
          console.warn(`Could not find step matching id ${typeof id} ${id}`);
        }
      }
	});
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/intake-step-list.component.ts